import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { PromptVersion } from '../../lib/prompts-api';

interface Props {
  open: boolean;
  versions: PromptVersion[];
  setting: string;
  onClose: () => void;
  onRevert: (versionId: number) => void;
}

export function PromptHistorySheet({ open, versions, setting, onClose, onRevert }: Props) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>History — {setting}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2 overflow-y-auto max-h-[80vh]">
          {versions.length === 0 && (
            <div className="text-sm text-muted-foreground py-4">
              No saved versions yet. The default markdown body is in use.
            </div>
          )}
          {versions.map((v, i) => {
            const prev = versions[i + 1];
            const delta = prev ? v.body.length - prev.body.length : v.body.length;
            const expanded = expandedId === v.id;
            return (
              <div
                key={v.id}
                data-testid="history-row"
                className={cn(
                  'border rounded-md p-3',
                  v.isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white',
                )}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-2 text-left text-sm"
                  onClick={() => setExpandedId(expanded ? null : v.id)}
                >
                  {expanded
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />}
                  <span className="font-semibold">v{v.id}</span>
                  <span className="text-muted-foreground">
                    {new Date(v.createdAt).toLocaleString()} · {v.createdBy}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {v.body.length} chars{delta !== v.body.length && delta !== 0 && (
                      <> · <span className={delta > 0 ? 'text-green-600' : 'text-red-600'}>
                        {delta > 0 ? '+' : ''}{delta}
                      </span></>
                    )}
                  </span>
                  {v.isActive && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100">current</Badge>
                  )}
                </button>
                {expanded && (
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] font-mono bg-white border border-gray-200 rounded p-2">
                    {v.body}
                  </pre>
                )}
                {!v.isActive && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      data-testid="revert-button"
                      size="sm"
                      variant="outline"
                      onClick={() => onRevert(v.id)}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" /> Revert to this version
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
